import * as React from "react";
import * as styles from "./TreeSunlight.css";
import { Sunlight } from "./Sunlight/Sunlight";
import { AnimatedLeafClusters } from "./AnimatedLeafClusters";
import Ambience from "source/app/services/Sound/Ambience/Ambience";

type FullscreenElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type FullscreenDocument = Document & {
    webkitExitFullscreen?: () => Promise<void> | void;
    webkitFullscreenElement?: Element | null;
};

const BaseCanopyImage = "/images/backgrounds/treeSunlight/treeSunlight-base.png";
const MidCanopyImage = "/images/backgrounds/treeSunlight/treeSunlight-mid.png";
const NearCanopyImage = "/images/backgrounds/treeSunlight/treeSunlight-near.png";
const DappleCanopyImage = "/images/backgrounds/treeSunlight/treeSunlight-dapple.png";
const LeafClusterImage = "/images/backgrounds/treeSunlight/treeSunlight-leafClusters.png";

export function TreeSunlight() {
    const [sunCycle, setSunCycle] = React.useState(0);
    const [isAmbiencePlaying, setIsAmbiencePlaying] = React.useState(false);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const demoRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const updateAmbienceState = () => {
            const isPlaying = Ambience.IsWoodlandAmbiencePlaying();
            setIsAmbiencePlaying((previous) =>
                previous === isPlaying ? previous : isPlaying
            );
        };

        updateAmbienceState();
        const intervalId = window.setInterval(updateAmbienceState, 250);
        return () => window.clearInterval(intervalId);
    }, []);

    React.useEffect(() => {
        const updateFullscreenState = () => {
            setIsFullscreen(IsFullscreen());
        };

        document.addEventListener("fullscreenchange", updateFullscreenState);
        document.addEventListener("webkitfullscreenchange", updateFullscreenState);
        window.addEventListener("resize", updateFullscreenState);
        updateFullscreenState();
        return () => {
            document.removeEventListener("fullscreenchange", updateFullscreenState);
            document.removeEventListener("webkitfullscreenchange", updateFullscreenState);
            window.removeEventListener("resize", updateFullscreenState);
        };
    }, []);

    const toggleFullscreen = async () => {
        const fullscreenDocument = document as FullscreenDocument;
        try {
            if (IsFullscreen()) {
                if (document.exitFullscreen) await document.exitFullscreen();
                else if (fullscreenDocument.webkitExitFullscreen) await fullscreenDocument.webkitExitFullscreen();
                return;
            }
            const element = demoRef.current as FullscreenElement | null;
            if (!element) return;
            if (element.requestFullscreen) await element.requestFullscreen();
            else if (element.webkitRequestFullscreen) await element.webkitRequestFullscreen();
        } catch {
            setIsFullscreen(false);
        }
    };

    return (
        <div ref={demoRef} className={styles.demo}>
            <div
                key={sunCycle}
                className={styles.scene}
                role="img"
                aria-label="Warm sunlight filtering through a layered tree canopy"
                style={{
                    "--tree-sunlight-base": `url("${BaseCanopyImage}")`,
                    "--tree-sunlight-mid": `url("${MidCanopyImage}")`,
                    "--tree-sunlight-near": `url("${NearCanopyImage}")`,
                    "--tree-sunlight-dapple": `url("${DappleCanopyImage}")`,
                    "--tree-sunlight-leaf-mask": `url("${LeafClusterImage}")`,
                } as React.CSSProperties}
            >
                <CanopyLayers />
                <Sunlight />
                <AnimatedLeafClusters imageSrc={LeafClusterImage} />
                <DustMotes />
            </div>
            <div className={styles.actions}>
                <button
                    className={styles.replayButton}
                    type="button"
                    onClick={() => setSunCycle((cycle) => cycle + 1)}
                >
                    Replay sunlight
                </button>
                <button
                    className={styles.replayButton}
                    type="button"
                    aria-pressed={isAmbiencePlaying}
                    onClick={() => Ambience.ToggleWoodlandAmbience()}
                >
                    {isAmbiencePlaying ? "Pause Woodlands" : "Play Woodlands"}
                </button>
                <button
                    className={styles.fullscreenButton}
                    type="button"
                    aria-pressed={isFullscreen}
                    onClick={() => void toggleFullscreen()}
                >
                    {isFullscreen ? "Exit full screen" : "Full screen"}
                </button>
            </div>
        </div>
    );
}

function CanopyLayers() {
    return (
        <>
            <span className={styles.canopyBase} aria-hidden="true" />
            <span className={styles.canopyMid} aria-hidden="true" />
            <span className={styles.canopyNearLeft} aria-hidden="true" />
            <span className={styles.canopyNearRight} aria-hidden="true" />
            <span className={styles.canopyNearTop} aria-hidden="true" />
            <span className={styles.canopyGustLeft} aria-hidden="true" />
            <span className={styles.canopyGustRight} aria-hidden="true" />
            <span className={styles.canopyDapple} aria-hidden="true" />
            <span className={styles.canopyDappleAlt} aria-hidden="true" />
        </>
    );
}

function DustMotes() {
    return (
        <span className={styles.dustLayer} aria-hidden="true">
            {Array.from({ length: 22 }, (_, index) => {
                const style = {
                    "--dust-x": `${11 + ((index * 37) % 79)}%`,
                    "--dust-y": `${10 + ((index * 61) % 82)}%`,
                    "--dust-size": `${0.7 + ((index * 13) % 18) / 10}px`,
                    "--dust-delay": `${-((index * 7) % 31)}s`,
                    "--dust-duration": `${24 + ((index * 11) % 19)}s`,
                } as React.CSSProperties;
                return <span key={index} className={styles.dust} style={style} />;
            })}
        </span>
    );
}

function IsFullscreen() {
    const fullscreenDocument = document as FullscreenDocument;
    return Boolean(document.fullscreenElement || fullscreenDocument.webkitFullscreenElement);
}
